import { useRef, useState } from "react"
import { InputBase, MenuItem, Popover, Stack, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"

import { RecentDish, useWeekMenu } from "../../WeekMenuContext"
import { cellFieldMissingStyle, cellFieldStyle } from "./cellFieldStyles"

const useStyles = makeStyles()((theme) => ({
    input: {
        ...cellFieldStyle(theme),
        width: "100%",
        padding: ".25rem",
        color: theme.palette.tailwind.slate[800],
        fontWeight: 700,
        fontSize: ".875rem",
        "& textarea::placeholder": {
            color: theme.palette.tailwind.slate[500],
            fontWeight: 400,
            opacity: 1,
        },
    },
    inputError: cellFieldMissingStyle(theme),
    suggestionsTitle: {
        padding: ".375rem .5rem .125rem",
        color: theme.palette.tailwind.gray[400],
        textTransform: "uppercase",
        fontWeight: 700,
    },
    suggestionItem: {
        display: "block",
    },
    suggestionMeta: {
        color: theme.palette.tailwind.gray[400],
    },
}))

type Props = Readonly<{
    value: string
    hasError: boolean
    onChange: (value: string) => void
    /** Picking a previous menu fills the whole dish. */
    onPickDish: (dish: RecentDish) => void
}>

export function CellTitleField({ value, hasError, onChange, onPickDish }: Props) {
    const { classes, cx } = useStyles()
    const { l10n } = useAppLocalization()
    const { getRecentDishes } = useWeekMenu()

    const anchorRef = useRef<HTMLDivElement | null>(null)
    const [showSuggestions, setShowSuggestions] = useState(false)

    const query = value.trim().toLowerCase()
    const suggestions = getRecentDishes()
        .filter((dish) => dish.title.toLowerCase() !== query)
        .filter((dish) => query === "" || dish.title.toLowerCase().includes(query))
        .slice(0, 7)

    const isSuggestionsOpen = showSuggestions && suggestions.length > 0

    const sectionTitle =
        query === ""
            ? l10n.getString("merchant-week-menu-cell-recent-dishes-title")
            : l10n.getString("merchant-week-menu-recent-dishes-matching-title", { query: value.trim() })

    return (
        <>
            <LocalizedStrict id="merchant-week-menu-cell-title-field" attrs={{ placeholder: true }}>
                <InputBase
                    ref={anchorRef}
                    multiline
                    value={value}
                    placeholder="Write dish name…"
                    className={cx(classes.input, hasError && classes.inputError)}
                    onChange={(event) => onChange(event.target.value)}
                    onFocus={() => setShowSuggestions(true)}
                    onBlur={() => setShowSuggestions(false)}
                />
            </LocalizedStrict>
            <Popover
                open={isSuggestionsOpen}
                anchorEl={anchorRef.current}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                disableAutoFocus
                disableEnforceFocus
                onClose={() => setShowSuggestions(false)}
            >
                <Typography variant="caption" component="div" className={classes.suggestionsTitle}>
                    {sectionTitle}
                </Typography>
                {suggestions.map((dish) => (
                    <MenuItem
                        key={dish.title}
                        dense
                        className={classes.suggestionItem}
                        onMouseDown={(event) => {
                            event.preventDefault()
                            onPickDish(dish)
                            setShowSuggestions(false)
                        }}
                    >
                        <Stack>
                            <Typography variant="body2">{dish.title}</Typography>
                            <Typography variant="caption" className={classes.suggestionMeta}>
                                {l10n.getString("merchant-week-menu-recent-dishes-meta")}
                                {dish.price
                                    ? ` ${l10n.getString("merchant-week-menu-recent-dish-price", { price: dish.price })}`
                                    : ""}
                            </Typography>
                        </Stack>
                    </MenuItem>
                ))}
            </Popover>
        </>
    )
}
